// CTSK-170 Part B — Access-request ingest schemas.
//
// The 0.1.55 access-request path (CTSK-166 Phase C) shipped a sparse
// 6-field tasko_request_access signature (sourceTag, clientKind,
// requestedCapabilities, title, rationale, container) that lands in
// External Clients as a doc whose frontmatter carries only source_tag /
// client_kind / requested_capabilities. Operators approving the request
// have no metadata beyond that — session UUIDs, working directory,
// model version, and every other bit of session context was flattened
// out. That's what Part B fixes.
//
// This module exposes the SHARED shape of the rich request. Same schema
// used by:
//
//   - tasko-mcp (input validation on the tasko_request_access tool)
//   - @captain-tasko/api-client (typed method signature + wire-shape
//     translator)
//   - the API server's ingest route (validation of the flat-projected
//     shape after api-client unwinds camelCase → snake_case frontmatter)
//
// Wire convention:
//   Tool API — camelCase, nested objects (sessionMetadata, clientMetadata,
//   stats). Ergonomic for a caller assembling one payload.
//   Wire     — flat snake_case, projected into `meta.frontmatter.*` by
//   the api-client translator. Compatible with the existing frontmatter-
//   projection CP layer (see `seed/bundles/external-clients-nest.bundle.
//   json` §customProperties.*.source.from = 'frontmatter').
//
// Fields split across four objects mirroring the seed bundle's CP
// grouping (session-context / client-kind-specific / stats / audit).
// See the bundle at v0.2.0 for the canonical CP catalog.
import { z } from 'zod';
import { SESSION_TAG_REGEX } from './pat.js';
// ─── Client kind ─────────────────────────────────────────────────────
/** The five external-client categories the Nest currently recognizes.
 *  Adds/removals go in ONE PLACE: this enum + the matching `client/*`
 *  tag rows in the seed bundle. Wire shape kebab-case, matches tag names
 *  and the `client_kind` CP options. */
export const AccessRequestClientKindSchema = z.enum([
    // CTSK-207 (0.1.63+): canonical name for any Cowork session (local OR
    // cloud). Prefer this in all new clients. `claude-mcp-session` retained
    // as a deprecated alias — same identity model, different naming era.
    'cowork-session',
    // DEPRECATED (CTSK-207, 0.1.63) — retained for one release cycle.
    // Predates the local/cloud distinction. Server + client accept it as
    // an alias for cowork-session but with the legacy required-field
    // subset (see ACCESS_REQUEST_REQUIRED_FIELDS_BY_KIND — includes
    // workingDir). New callers SHOULD emit `cowork-session` instead.
    'claude-mcp-session',
    'webhook-consumer',
    'ci-bot',
    'external-mcp',
    'human-tool',
]);
// ─── Session metadata (claude-mcp-session clients) ──────────────────
/** Extra context a Cowork Claude session carries alongside its access
 *  request. All optional at the schema layer; per-kind required-field
 *  validation is enforced by the server at ingest (B/3). Every field
 *  projects to a snake_case CP on the doc — see the corresponding CP
 *  description in the bundle for what "empty" means in each case.
 *
 *  Wire projection (camelCase → snake_case):
 *    accountUuid         → account_uuid       (CTSK-207 canonical; was workspaceUuid)
 *    organizationUuid    → organization_uuid  (CTSK-207 canonical; was sessionUuid)
 *    sessionKey          → session_key        (CTSK-207 canonical; was subUuid — accepts local_<uuid> OR cse_<opaque>)
 *    workspaceUuid       → workspace_uuid     (DEPRECATED 0.1.63; kept for one release)
 *    sessionUuid         → session_uuid       (DEPRECATED 0.1.63; kept for one release)
 *    subUuid             → sub_uuid           (DEPRECATED 0.1.63; kept for one release)
 *    workingDir          → working_dir
 *    sessionStartedAt    → session_started_at
 *    lastActivityAt      → last_activity_at
 *    modelVersion        → model_version
 *    coworkVersion       → cowork_version
 *    hostOs              → host_os
 *    hostname            → hostname
 *
 *  Backward-compat rule (CTSK-207): a session may send EITHER the new
 *  or the old field name for each pair — server prefers the new name
 *  when both are present. See resolveCanonicalSessionMetadata() below.
 *  Once existing sessions migrate, the deprecated fields will be
 *  removed in a future release.
 */
export const AccessRequestSessionMetadataSchema = z.object({
    // CTSK-207 canonical names (0.1.63+).
    /** Invariant Anthropic account UUID for the session's origin.
     *  Renamed from workspaceUuid per v3 env-probe semantics
     *  (LOCAL path `<account_uuid>/<organization_uuid>/local_<session>/`;
     *  CLOUD env `CLAUDE_CODE_ACCOUNT_UUID`). */
    accountUuid: z.string().min(1).max(200).optional(),
    /** Invariant Anthropic organization UUID within the account.
     *  Renamed from sessionUuid per v3 env-probe semantics
     *  (LOCAL path second segment; CLOUD env `CLAUDE_CODE_ORGANIZATION_UUID`). */
    organizationUuid: z.string().min(1).max(200).optional(),
    /** Canonical session_key. Two accepted shapes (CTSK-208): `local_<uuid>`
     *  for local Cowork sessions, `cse_<opaque>` for cloud sessions.
     *  Server derives session_key_tail_first8 (also stamped as
     *  sub_uuid_tail_first8 during transition). Renamed from subUuid. */
    sessionKey: z.string().min(1).max(200).optional(),
    // DEPRECATED aliases (CTSK-207 sunset countdown; kept for one release cycle).
    /** @deprecated Use accountUuid instead. Server accepts either; if
     *  both are present, accountUuid wins. Removed in a future release. */
    workspaceUuid: z.string().min(1).max(200).optional(),
    /** @deprecated Use organizationUuid instead. Server accepts either. */
    sessionUuid: z.string().min(1).max(200).optional(),
    /** @deprecated Use sessionKey instead. Server accepts either. */
    subUuid: z.string().min(1).max(200).optional(),
    workingDir: z.string().min(1).max(4096).optional(),
    /** ISO 8601 UTC timestamp — Cowork session start. */
    sessionStartedAt: z.string().min(1).max(64).optional(),
    /** ISO 8601 UTC timestamp — most-recent activity the session self-
     *  reports. Refreshed by idempotent replay via externalId. */
    lastActivityAt: z.string().min(1).max(64).optional(),
    /** Model identifier (e.g. "claude-opus-4-7"). */
    modelVersion: z.string().min(1).max(200).optional(),
    /** Cowork host app version. */
    coworkVersion: z.string().min(1).max(200).optional(),
    /** Same shape as node's process.platform (darwin, linux, win32, etc.). */
    hostOs: z.string().min(1).max(64).optional(),
    hostname: z.string().min(1).max(255).optional(),
});
// ─── Client metadata (non-session client kinds) ─────────────────────
/** Fields specific to non-session client kinds. Which subset is required
 *  depends on `clientKind`; the schema layer keeps everything optional
 *  and the server enforces the per-kind rules at B/3.
 *
 *  Wire projection:
 *    endpointUrl      → endpoint_url
 *    ciSystem         → ci_system
 *    ciJobId          → ci_job_id
 *    displayName      → display_name
 *    orgAffiliation   → org_affiliation
 */
export const AccessRequestClientMetadataSchema = z.object({
    /** For webhook-consumer: destination URL for callbacks. Required at
     *  ingest when clientKind === 'webhook-consumer'. */
    endpointUrl: z.string().url().max(2048).optional(),
    /** For ci-bot: CI system identifier (`gitlab-ci`, `github-actions`,
     *  etc.). Required at ingest when clientKind === 'ci-bot'. */
    ciSystem: z.string().min(1).max(64).optional(),
    /** For ci-bot: pipeline / run id. Required with ciSystem. */
    ciJobId: z.string().min(1).max(200).optional(),
    /** For external-mcp / human-tool: friendly display name. Required at
     *  ingest for those two kinds. */
    displayName: z.string().min(1).max(200).optional(),
    /** For external-mcp / human-tool: external org affiliation
     *  (company / GitLab group / GitHub org). Optional. */
    orgAffiliation: z.string().min(1).max(200).optional(),
});
// ─── Stats (advisory, self-reported) ────────────────────────────────
/** Self-reported lifetime activity counters. All advisory — the operator
 *  reads them for context ("this session is active enough to bother re-
 *  approving") but they don't gate approval. Refreshed on every
 *  tasko_request_access idempotent replay.
 *
 *  Wire projection:
 *    workitemsTouched     → workitems_touched
 *    commentsPosted       → comments_posted
 *    transitionsExecuted  → transitions_executed
 *    tokenBudgetUsed      → token_budget_used
 *    lastEnvSyncedAt      → last_env_synced_at
 */
export const AccessRequestStatsSchema = z.object({
    workitemsTouched: z.number().int().nonnegative().max(1e9).optional(),
    commentsPosted: z.number().int().nonnegative().max(1e9).optional(),
    transitionsExecuted: z.number().int().nonnegative().max(1e9).optional(),
    tokenBudgetUsed: z.number().int().nonnegative().max(1e12).optional(),
    /** ISO 8601 UTC. */
    lastEnvSyncedAt: z.string().min(1).max(64).optional(),
});
// ─── Top-level ingest shape ─────────────────────────────────────────
/** Rich `tasko_request_access` signature (CTSK-170 Part B).
 *
 *  Backward-compat with the 0.1.55 sparse signature: everything the old
 *  path required (`sourceTag`, `rationale`) is still required here; the
 *  old-path optional fields (`title`, `clientKind`, `container`) stay
 *  optional. The Part B additions (`role`, `purpose`, sessionMetadata,
 *  clientMetadata, stats, externalId) are all optional at the schema
 *  layer.
 *
 *  Per-kind required-field enforcement (see the ticket's §"Required
 *  fields per clientKind" table) happens at B/3 in the server, not
 *  here — the shape schema stays permissive so the client-side
 *  validation can be either a superset or subset of the server's
 *  rules without the schema fighting either. */
export const AccessRequestIngestSchema = z.object({
    // ── Identity (existing 0.1.55 core, still required) ─────────────
    // CTSK-430: was length-only (min(1).max(200)), which let mixed-case
    // tags through and produced CTSK-338-class case-duplicates
    // (`dotcom-worker` vs `DOTcom-worker`). Now pinned to the same
    // SESSION_TAG_REGEX that PersonalAccessTokenCreateSchema.sourceTag
    // already enforces on the direct POST /pats path.
    sourceTag: z.string().regex(SESSION_TAG_REGEX, {
        message: 'sourceTag must be kebab-case: lowercase letters, digits, hyphens; 2-64 chars; start with letter, end with letter or digit',
    }),
    rationale: z.string().min(1).max(10_000),
    // ── Existing 0.1.55 optionals ────────────────────────────────────
    title: z.string().min(1).max(500).optional(),
    clientKind: AccessRequestClientKindSchema.optional(),
    container: z.string().min(1).max(200).optional(),
    // ── B/3 role handle (supersedes requestedCapabilities except for
    //    the `custom` escape hatch) ─────────────────────────────────
    role: z.string().min(1).max(64).optional(),
    /** Still accepted for role === 'custom' AND for backward-compat with
     *  0.1.55 callers who pre-date the role catalog. When both `role`
     *  and `requestedCapabilities` are provided AND role !== 'custom',
     *  the server prefers the role's catalog caps and drops the array
     *  (per Part B/3 resolution rules). */
    requestedCapabilities: z.array(z.string().min(1).max(200)).max(50).optional(),
    // ── B/1 additions ─────────────────────────────────────────────
    /** One-line human-readable purpose summary. Renders as the "Purpose"
     *  section header in the server-composed doc body (B/5). */
    purpose: z.string().min(1).max(500).optional(),
    /** Structured session context. Required fields per-clientKind
     *  enforced at B/3. */
    sessionMetadata: AccessRequestSessionMetadataSchema.optional(),
    /** Structured non-session client context. Required fields per-
     *  clientKind enforced at B/3. */
    clientMetadata: AccessRequestClientMetadataSchema.optional(),
    /** Advisory self-reported stats. Never required. */
    stats: AccessRequestStatsSchema.optional(),
    // ── CTSK-296 addition (0.1.114+) ─────────────────────────────────
    /** Requester-side TTL preference for the minted PAT, in days.
     *  Optional; tasko-mcp 0.0.29+ injects 7 when the caller omits it,
     *  so self-request PATs default to a 7-day life instead of the
     *  role mintPolicy's (much longer) `expiresInDays`. Server clamps
     *  against the per-role `mintPolicy.maxExpiresInDays` (default 30)
     *  at approve time and reports `expiryClampedFrom` when the clamp
     *  fires. Docs without the CP fall back to `mintPolicy.expiresInDays`
     *  (today's implicit default, now explicit). Range-checked 1..90 at
     *  ingest — out-of-range → 400 before the doc is created. */
    requestedExpiresInDays: z.number().int().min(1).max(90).optional()
        .describe('Requester-side TTL preference in days. Optional; tasko-mcp defaults to 7 if omitted. Server may cap via mintPolicy.maxExpiresInDays.'),
    /** Importer-supplied stable id for idempotent retries. If provided,
     *  the server treats a second POST with the same (orgId, sourceTag,
     *  externalId) as an UPDATE to the existing doc rather than a new
     *  ingest. Standard round-trip identifier convention — matches
     *  workitems/comments patterns. */
    externalId: z.string().min(1).max(200).optional(),
    // ── CTSK-213 P6 addition (0.1.71+) ────────────────────────────────
    /** greetId shortcut (CTSK-213, follow-up to CTSK-210's tasko_greet).
     *  The ULID returned by `tasko_greet`. When set on a cowork-session
     *  request, the server resolves the introduction doc pinned to
     *  `(targetOrgId, greet_id)` and pulls `session_key` / `account_uuid`
     *  / `organization_uuid` (+ `intro_friendly_name`) from that doc as
     *  the CANONICAL identity — OVERRIDING any client-sent
     *  sessionMetadata fields (doc identity wins; anti-spoofing rule,
     *  AC#5). Either `greetId` OR `sessionMetadata.sessionKey` satisfies
     *  the cowork-session identity requirement — both paths are additive.
     *
     *  Server failure modes (both 400 VALIDATION_ERROR, discriminated by
     *  `details[0].code`):
     *    - `unknown_greetId`     — no live intro doc in the TARGET org
     *      matches. Cross-org misses are deliberately indistinguishable
     *      from true misses (greetIds are ULIDs, not secrets — the
     *      endpoint must not act as an existence oracle for other orgs'
     *      intro docs).
     *    - `stale_greet_intro`   — the intro doc predates CTSK-208
     *      canonical session_key shapes (no `session_key` CP). No
     *      normalization is attempted; the caller should re-greet. */
    greetId: z.string().min(1).max(64).optional(),
});
// ─── Wire projection helper ─────────────────────────────────────────
//
// The api-client translator uses this map to unwind the camelCase tool
// API into flat snake_case frontmatter keys the CP-projection layer
// expects. Kept in shared so tasko-mcp, the api-client, and any future
// consumer stay in lockstep — a single source of truth for "which
// camelCase field goes to which frontmatter key."
//
// Not all fields project one-to-one (e.g. `rationale` is the doc body,
// not a frontmatter key; `title` lands at `meta.title`, not
// `meta.frontmatter.*`). The projection table below covers only the
// fields that DO land in `meta.frontmatter.*`. See the api-client
// method for the fields that don't.
/** Field-name projection table for the rich ingest shape.
 *
 *  Key is the camelCase tool-API name; value is the snake_case
 *  frontmatter key the server-side CP-projection reads. Nested paths
 *  use dot notation on the reader side but flatten to a single key on
 *  the wire (e.g. `sessionMetadata.workspaceUuid` → `workspace_uuid`).
 *
 *  Where the source column names a nested object (sessionMetadata /
 *  clientMetadata / stats), the map lists ALL fields inside that
 *  object using dot-paths, so a translator can walk the input shape
 *  and emit the flat frontmatter using a simple lookup.
 *
 *  Does NOT include:
 *    - `title`      (lands at meta.title, not meta.frontmatter.*)
 *    - `rationale`  (becomes the doc body, not a CP)
 *    - `container`  (lands at meta.container, not meta.frontmatter.*)
 *    - `role`       (lands at meta.frontmatter.role — camelCase to
 *                    snake_case is a no-op)
 *    - `externalId` (goes on the ingest envelope itself, not the
 *                    frontmatter — TBD wire location; may end up on
 *                    a header or a top-level meta field. B/6 pins it.) */
export const ACCESS_REQUEST_FRONTMATTER_MAP = {
    // Identity
    sourceTag: 'source_tag',
    clientKind: 'client_kind',
    role: 'role_handle', // aligns with the doc's role_handle CP
    purpose: 'purpose',
    // CTSK-213 P6: greet shortcut — resolved server-side against the
    // (targetOrgId, greet_id) intro doc; projects to the greet_id CP.
    greetId: 'greet_id',
    // CTSK-296: requester-side TTL preference. Seeded into the doc's CPs
    // at INSERT time (CTSK-222 precedent — no dependency on installed
    // bundle CP definitions), read by approve-and-mint-pat at mint time.
    requestedExpiresInDays: 'requested_expires_in_days',
    // Cap array — stringified by api-client per the existing 0.1.55 hotfix pattern.
    requestedCapabilities: 'requested_capabilities',
    // Session metadata (client/cowork-session + legacy claude-mcp-session)
    // CTSK-207 canonical (0.1.63+):
    'sessionMetadata.accountUuid': 'account_uuid',
    'sessionMetadata.organizationUuid': 'organization_uuid',
    'sessionMetadata.sessionKey': 'session_key',
    // DEPRECATED aliases (0.1.63; kept for one release cycle):
    'sessionMetadata.workspaceUuid': 'workspace_uuid',
    'sessionMetadata.sessionUuid': 'session_uuid',
    'sessionMetadata.subUuid': 'sub_uuid',
    'sessionMetadata.workingDir': 'working_dir',
    'sessionMetadata.sessionStartedAt': 'session_started_at',
    'sessionMetadata.lastActivityAt': 'last_activity_at',
    'sessionMetadata.modelVersion': 'model_version',
    'sessionMetadata.coworkVersion': 'cowork_version',
    'sessionMetadata.hostOs': 'host_os',
    'sessionMetadata.hostname': 'hostname',
    // Client metadata (non-session)
    'clientMetadata.endpointUrl': 'endpoint_url',
    'clientMetadata.ciSystem': 'ci_system',
    'clientMetadata.ciJobId': 'ci_job_id',
    'clientMetadata.displayName': 'display_name',
    'clientMetadata.orgAffiliation': 'org_affiliation',
    // Stats
    'stats.workitemsTouched': 'workitems_touched',
    'stats.commentsPosted': 'comments_posted',
    'stats.transitionsExecuted': 'transitions_executed',
    'stats.tokenBudgetUsed': 'token_budget_used',
    'stats.lastEnvSyncedAt': 'last_env_synced_at',
};
// ─── Per-kind required-field table ──────────────────────────────────
//
// Used by both the client (early-feedback validation in tasko-mcp) and
// the server (B/3 authoritative gate). Shape mirrors the ticket's
// §"Required fields per clientKind" table.
//
// Each entry names camelCase dot-paths INTO the input shape — B/3
// resolves those paths against the tool-API object, not against the
// flat wire shape. That way the error message can point at a
// meaningful field name.
//
// Empty array === no additional required fields beyond the core
// (sourceTag, rationale) that AccessRequestIngestSchema itself enforces.
export const ACCESS_REQUEST_REQUIRED_FIELDS_BY_KIND = {
    // CTSK-207: canonical name. workingDir OPTIONAL per the workingDir
    // revisit — for cloud sessions it's a sandbox cwd with low operator-
    // signal value, and even for local sessions the field is purely
    // presentational (renders as a Session Context table row in the doc
    // body). Requiring it forced cloud sessions to send noise. The three
    // invariant anchors (accountUuid / organizationUuid / sessionKey)
    // remain required.
    //
    // CTSK-213 P6: EITHER this sessionMetadata triple OR `greetId` alone
    // satisfies the identity requirement. The table below stays the
    // sessionMetadata-path source of truth; the greetId path bypasses it
    // because the SERVER resolves the intro doc and merges the three
    // anchors into the frontmatter BEFORE the required-field check runs.
    // Client-side validators (tasko-mcp) should skip this check when
    // `greetId` is present — see ACCESS_REQUEST_GREETID_SATISFIES_KINDS.
    'cowork-session': [
        'sessionMetadata.accountUuid',
        'sessionMetadata.organizationUuid',
        'sessionMetadata.sessionKey',
    ],
    // DEPRECATED (0.1.63): legacy required set retained for backward
    // compat. New clients should emit `cowork-session` instead — same
    // identity model, lighter required subset. The server's validator
    // accepts new-name aliases (e.g. accountUuid → workspaceUuid via
    // ACCESS_REQUEST_FIELD_SNAKE_ALIASES) so a client sending the old
    // clientKind with new field names still passes.
    'claude-mcp-session': [
        'sessionMetadata.workspaceUuid',
        'sessionMetadata.sessionUuid',
        'sessionMetadata.subUuid',
        'sessionMetadata.workingDir',
    ],
    'webhook-consumer': ['clientMetadata.endpointUrl'],
    'ci-bot': ['clientMetadata.ciSystem', 'clientMetadata.ciJobId'],
    'external-mcp': ['clientMetadata.displayName'],
    'human-tool': ['clientMetadata.displayName'],
};
// ─── Field-name aliases (CTSK-207) ──────────────────────────────────
//
// Maps each snake_case frontmatter key to any equivalent aliases the
// server should accept as satisfying the same requirement. Bidirectional
// during the deprecation transition — sending EITHER key counts as
// present for the OR-check.
//
// Used by the ingest validator's required-field check: a claude-mcp-
// session sending the NEW field names still satisfies the legacy
// required set (workspace_uuid ↔ account_uuid, etc.), and a cowork-
// session sending the OLD field names still satisfies the new required
// set.
//
// Once existing clients migrate away from the deprecated names, the
// aliases can be removed alongside the deprecated schema fields.
export const ACCESS_REQUEST_FIELD_SNAKE_ALIASES = {
    account_uuid: ['workspace_uuid'],
    workspace_uuid: ['account_uuid'],
    organization_uuid: ['session_uuid'],
    session_uuid: ['organization_uuid'],
    session_key: ['sub_uuid'],
    sub_uuid: ['session_key'],
};
// ─── greetId identity shortcut (CTSK-213 P6) ────────────────────────
//
// Client kinds whose per-kind identity requirement is satisfied by a
// `greetId` ALONE (no sessionMetadata needed). Shared so tasko-mcp's
// early-feedback validator and any future client stay in lockstep with
// the server's rule:
//
//   - Client side: when `greetId` is present AND the clientKind is in
//     this list, SKIP the sessionMetadata required-fields check — the
//     server resolves identity from the (targetOrgId, greet_id) intro
//     doc and merges it into the frontmatter before its own required-
//     field gate runs.
//   - Server side: the ingest pre-processor (apps/api greet-intro
//     resolver) performs the lookup + merge; the required-fields table
//     above then passes naturally on the merged surface.
//
// Non-listed kinds ignore `greetId` for requirement purposes (the field
// still projects to the greet_id CP if sent, but doesn't waive any
// required fields).
export const ACCESS_REQUEST_GREETID_SATISFIES_KINDS = [
    'cowork-session',
];
// ─── PAT lifecycle summary (CTSK-476) ────────────────────────────────
//
// Per-row cross-reference of the linked PAT's *credential* state, served
// alongside the doc-derived `state` on every AccessRequestSummary from
// GET /nests/:slug/access-requests (self-scoped) and /admin. The two
// axes drift on purpose: `state` is the doc's `access/*` tag (the audit
// record — "this request was approved"), `patLifecycle.state` is what
// personal_access_tokens says *right now* ("…and the credential it
// minted is live / revoked / expired"). Out-of-band revokes (DELETE
// /pats/:id, the CTSK-414 rotation sweep, manual SQL) touch only the
// PAT row, so an `approved` doc can carry a `revoked` credential — this
// field is how the Nest UI tells them apart without a DB round-trip.
//
//   live         — linked PAT exists, revoked_at IS NULL, not past expires_at
//   revoked      — linked PAT has revoked_at set (wins over expired)
//   expired      — linked PAT has expires_at in the past
//   never_minted — doc has no `linked_pat_id` CP, or it points at a PAT
//                  row that no longer exists (pending / denied rows;
//                  on an `approved` row this is a data inconsistency)
//
// `.strict()` so an accidental extra key on the wire fails loudly in
// clients that parse with the schema rather than silently widening the
// contract. See CTSK-181 (revoke-client-pat.ts) for the in-band path
// that keeps doc + PAT state converged, and CTSK-455 Phase 4 for the
// planned out-of-band hook.
export const PAT_LIFECYCLE_STATES = ['live', 'revoked', 'expired', 'never_minted'];
export const PatLifecycleSummarySchema = z
    .object({
    state: z.enum(PAT_LIFECYCLE_STATES),
    /** ISO 8601 UTC from personal_access_tokens.revoked_at; null unless revoked. */
    revokedAt: z.string().nullable(),
    /** ISO 8601 UTC from personal_access_tokens.expires_at; null = no expiry
     *  or never minted. Present for `live` rows too so the UI can render
     *  an expiry countdown without a second lookup. */
    expiresAt: z.string().nullable(),
})
    .strict();
/** Pure classifier shared by the API route and the web tests — one
 *  precedence order (revoked > expired > live) in one place. `now`
 *  is injectable for deterministic tests. */
export function classifyPatLifecycle(pat, now = Date.now()) {
    if (!pat)
        return { state: 'never_minted', revokedAt: null, expiresAt: null };
    if (pat.revokedAt)
        return { state: 'revoked', revokedAt: pat.revokedAt, expiresAt: pat.expiresAt };
    if (pat.expiresAt) {
        const expMs = new Date(pat.expiresAt).getTime();
        if (!Number.isNaN(expMs) && expMs < now) {
            return { state: 'expired', revokedAt: null, expiresAt: pat.expiresAt };
        }
    }
    return { state: 'live', revokedAt: null, expiresAt: pat.expiresAt };
}
//# sourceMappingURL=access-request.js.map